import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HelpCircle, Zap, Trophy, Dices, ArrowDown, Play } from "lucide-react";
import PhoneMockup from "./PhoneMockup";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ playstoreUrl }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  const d1x = useTransform(sx, (v) => v * 46);
  const d1y = useTransform(sy, (v) => v * 46);
  const d2x = useTransform(sx, (v) => v * -70);
  const d2y = useTransform(sy, (v) => v * -70);
  const d3x = useTransform(sx, (v) => v * 24);
  const d3y = useTransform(sy, (v) => v * 24);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={onMove}
      className="grid-lines relative flex min-h-screen items-center overflow-hidden px-4 pt-32 pb-20 sm:px-8"
    >
      <div className="hero-blob absolute inset-0" />
      <div className="noise-overlay absolute inset-0" />

      <motion.div style={{ x: d1x, y: d1y }} className="absolute left-[6%] top-[22%] hidden lg:block">
        <div className="glass animate-floaty flex h-20 w-20 items-center justify-center rounded-3xl">
          <HelpCircle size={34} className="text-[#00F0FF]" />
        </div>
      </motion.div>
      <motion.div style={{ x: d2x, y: d2y }} className="absolute right-[8%] top-[16%] hidden lg:block">
        <div className="glass animate-floaty flex h-16 w-16 items-center justify-center rounded-2xl" style={{ animationDelay: "0.8s" }}>
          <Zap size={26} className="text-[#39FF14]" fill="#39FF14" />
        </div>
      </motion.div>
      <motion.div style={{ x: d2x, y: d1y }} className="absolute bottom-[16%] left-[10%] hidden lg:block">
        <div className="glass animate-floaty flex h-14 w-14 items-center justify-center rounded-2xl" style={{ animationDelay: "1.6s" }}>
          <Dices size={24} className="text-[#FF007A]" />
        </div>
      </motion.div>
      <motion.div style={{ x: d1x, y: d2y }} className="absolute bottom-[24%] right-[6%] hidden lg:block">
        <div className="glass animate-floaty flex h-16 w-16 items-center justify-center rounded-full" style={{ animationDelay: "2.4s" }}>
          <Trophy size={24} className="text-[#8A2BE2]" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-code text-xs tracking-[0.3em] text-[#00F0FF]"
          >
            ANDROID EXCLUSIVE — FREE TO PLAY
          </motion.p>
          <h1
            data-testid="hero-title-masked"
            className="font-display mt-6 text-4xl font-extrabold leading-[1.08] sm:text-5xl lg:text-[3.2rem]"
          >
            <MaskedLine delay={0.25}>THE ULTIMATE</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-glow-cyan">TRIVIA</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-stroke-magenta whitespace-nowrap pr-2">BATTLEGROUND</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-6 max-w-md text-base text-[#94A3B8]"
          >
            Real-time quiz duels, an engine that learns how you think, and a
            global leaderboard waiting for your name. Your brain is the
            controller.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={playstoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-playstore-cta"
              className="btn-neon animate-pulse-ring flex items-center gap-2.5 rounded-full bg-[#00F0FF] px-7 py-3.5 font-sub text-sm font-bold text-[#08090E]"
            >
              <Play size={16} fill="currentColor" />
              Get it on Google Play
            </a>
            <a
              href="#play"
              data-testid="hero-try-quiz-cta"
              className="btn-neon flex items-center gap-2.5 rounded-full border border-[#222738] px-7 py-3.5 font-sub text-sm font-semibold text-white hover:border-[#FF007A]"
            >
              Try a question
              <ArrowDown size={15} />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div style={{ x: d3x, y: d3y }}>
            <PhoneMockup />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
